/**
 * Zod schemas for the patient form (E4). These are the single source of truth
 * for the *shape and rules* of a patient/task payload, shared so the web form
 * (E4.1–E4.5) and the API validate identically — the API's class-validator DTOs
 * mirror these rules field-for-field, and the constants below (the enum value
 * lists, the alert cap) are imported by both sides so they can't drift.
 *
 * Kept dependency-light: only `zod`. No imports from the API or drizzle.
 */
import { z } from "zod";

import type { Priority, TaskUrgency } from "../db.js";

import { HIGH_PRIORITY_TASK_MESSAGE } from "./readiness.js";

// Runtime value lists for the enums whose *types* live in `db.ts`. The
// `satisfies` clause makes TypeScript fail the build if either list drifts from
// the union it mirrors.
export const PRIORITIES = ["high", "medium", "low"] as const satisfies readonly Priority[];
export const TASK_URGENCIES = [
  "urgent",
  "soon",
  "routine",
] as const satisfies readonly TaskUrgency[];

/** Max length of the optional free-text clinical alert (E4.4). */
export const ALERT_MAX_LENGTH = 140;

/** A required free-text field that must be non-empty after trimming. */
const requiredText = z.string().trim().min(1, "This field is required");

/** The four SBAR fields — each required and non-empty after trim (E4.2). */
export const sbarSchema = z.object({
  situation: requiredText,
  background: requiredText,
  assessment: requiredText,
  recommendation: requiredText,
});

export type SbarInput = z.infer<typeof sbarSchema>;

/** A single outstanding task on a patient (E4.3). */
export const taskInputSchema = z.object({
  description: requiredText,
  urgency: z.enum(TASK_URGENCIES),
});

export type TaskInput = z.infer<typeof taskInputSchema>;

/**
 * A full patient payload, as submitted by the create/update form. The cross-
 * field rule "a high-priority patient must carry at least one task" (E4.3) is a
 * `superRefine` so the error is attached to the `tasks` field for the form.
 */
export const patientInputSchema = z
  .object({
    hospitalNumber: requiredText,
    fullName: requiredText,
    // The DB column is a `date` (YYYY-MM-DD); keep the wire format to the day.
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date of birth must be a YYYY-MM-DD date"),
    bedNumber: requiredText,
    diagnosis: requiredText,
    priority: z.enum(PRIORITIES),
    assignedDoctorId: z.string().uuid().optional(),
    alert: z.string().trim().max(ALERT_MAX_LENGTH).optional(),
    sbar: sbarSchema,
    tasks: z.array(taskInputSchema),
  })
  .superRefine((value, ctx) => {
    if (value.priority === "high" && value.tasks.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tasks"],
        message: HIGH_PRIORITY_TASK_MESSAGE,
      });
    }
  });

export type PatientInput = z.infer<typeof patientInputSchema>;
